import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActionArea,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { 
  PlayArrow as PlayIcon,
  Description as DocumentIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  AssessmentOutlined as AssessmentIcon,
  BarChart as BarChartIcon,
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminIcon,
  NoteAlt as NotepadIcon,
  LocalFireDepartment as IgnitionIcon,
  Close as CloseIcon,
  Feedback as FeedbackIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface GuideProps {}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`guide-tabpanel-${index}`}
      aria-labelledby={`guide-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface VideoGuide {
  id: string;
  title: string;
  description: string;
  category: string;
  embedUrl: string;
  duration?: string;
  transcript?: string;
}

interface TextGuide {
  id: string;
  title: string;
  content: string;
  category: string;
  icon: React.ReactNode;
}

export const Guides: React.FC<GuideProps> = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState<VideoGuide | null>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleVideoClick = (guide: VideoGuide) => {
    setSelectedVideo(guide);
    setIsVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  const videoGuides: VideoGuide[] = [
    {
      id: 'dashboard-overview',
      title: 'Dashboard Overview',
      description: 'Learn how to use the dashboard to track key metrics and client progress',
      category: 'Dashboard Overview',
      embedUrl: 'https://player.vimeo.com/video/1074557559?h=eb235fd1df&badge=0&autopause=0&player_id=0&app_id=58479',
      duration: '9:51',
      transcript: `This is the dashboard component, which I’ll run through first. I’ll walk through each section separately to provide context and explain what’s being measured and recorded.

Starting from the top, there’s a series of summary tiles that give a quick overview of key metrics - especially those that may require immediate attention or action.

Total Clients
This shows the number of clients who have been active since 1 January 2025. Only those who have attended a session on or after this date are counted. If someone hasn't had a session since then, they won’t be included. However, if they attend a session in the future, they’ll automatically be added and their historical data will become visible in the system. This approach ensures data hygiene by excluding inactive clients.

Active Packages with Pending Reports
This is a count of clients who have submitted all the required information for either an initial or review Ignition Program report, but the report hasn’t yet been generated. It indicates there’s a need to create and send the report.

Success Updates Required
This shows clients who need a success update and action from you. I’ll explain the criteria for this category in more detail later.

Upcoming Sessions
This displays clients with sessions scheduled for today or tomorrow.

Components Overview

Ignition Client Alerts
This section highlights clients who have reached key milestones in their 2025 Ignition Program but haven't completed required sessions. The three milestones are:

Two weeks after the first session: If they haven’t completed sessions like the metabolic screen, physical performance screen, or nutrition session.

Six weeks (halfway point): If they haven’t completed their second nutrition session.

Twelve weeks (end of program): If any required sessions are still outstanding.

You’ll see a tile for each flagged client, detailing which sessions are missing. Clicking the tile opens the client profile, where you can view more information and follow up to help them complete the necessary sessions and enable report generation.

Pending Reports
Clients who’ve met all criteria for report generation but don’t yet have a report will appear here. You can click the tile to go directly to their profile and generate the report.

Upcoming Sessions
This shows sessions booked for today and tomorrow. If there are many, a “Show More” button will appear for an expanded view. Clicking on a client tile opens their profile for quick access to session and background information.

Recent Sessions
This section lists clients in descending order based on their most recent session. If there are more than six, you can expand or collapse the list.

Success Updates Required
This highlights clients who haven't had a success update in six weeks or more and currently have an active package. Inactive clients are removed from this list. Clicking a tile takes you to the client profile, where you can submit the update. Clients with no historical success updates won’t trigger alerts until one has been added. Over time, this will update as more clients are onboarded into the new system.

Recent Subscription Cancellations
This lists clients who have recently canceled their subscriptions, consolidating information that might otherwise only come through email.

Performance Indicators

There are four key performance indicators:

Ignition Conversion Rate – how many clients who began an Ignition Program have gone on to purchase another program.

First Cycle Conversion Rate – includes clients who started with any entry program (4-, 6-, or 12-week). Often overlaps with the Ignition rate.

NPS Score – average of Net Promoter Score and feedback submitted after six weeks.

Average Sessions per Client – number of sessions per client on your list.

Sessions per Month
This component shows a monthly breakdown of all session types (1:1, group, classes) over the last 13 months. It gives a high-level view of monthly session volume.
`
    },
    {
      id: 'client-management',
      title: 'Client Management',
      description: 'Learn how to add, update, and manage client information',
      category: 'Client Management',
      embedUrl: 'https://player.vimeo.com/video/1074563025?h=db98da1a54&badge=0&autopause=0&player_id=0&app_id=58479',
      duration: '16:40',
      transcript: `This is the Client Overview tab within the Client Management System. It provides a list and breakdown of the total clients visible in the tile within the dashboard. Within the Client Overview, there are essentially two sections: your clients and collaborator clients.

The difference between these two sections is that your clients are a list of clients where you are the lead coach, defined as the coach the client has seen most frequently within their most recent twelve visits. Collaborator clients, on the other hand, are any other clients you've seen but are not the lead coach of. For example, if you've covered a session for a colleague or provided accessory assessments at the beginning or end of the Ignition program, such as valve assessments, metabolic screening, or nutrition sessions, you'll see these clients appear in the collaborator clients section.

Although the information contained within this Client Overview is the same for both groups, it's clearly differentiated between them. You have the client ID, client name, date of the first visit, and most recent visit, along with the name of their lead coach. Obviously, for your clients, that's going to be you. For collaborative clients, you can easily track who the lead coaches are, their location, and their report status, which is more relevant for the Ignition program 2025 clients. This allows you to track whether they've had all their prerequisites, report information submitted, and if it's ready to generate or has been generated. You can also see their status, whether they currently have an active package or not, and their conversion status, which indicates whether they've made a subsequent package beyond their first package or not.

If you have a list of clients that's more than ten, you can either increase the number of rows visible or use the arrows to scroll backwards and forwards between the two elements of the list. If there's a specific client you're looking for, you can search for them by their name, ID, or email, which will filter the list to that specific client. This feature becomes more important as the client list grows. Additionally, you have the ability to filter between active and inactive clients.

When you click on any of those clients within the Client Overview page or on any of the tiles within the dashboard, you'll be redirected to the client-specific details page, which is here. This is a dummy account. This page contains all the information a real client would have, and it will be the main reference page for individual client management. I'll run through the contents of what each of these pages will include.

At the top, you'll have the client's name, indicating whether they currently have an active package or not, their client ID number, their most recent attendance, and their usually frequented location. You'll also see whether they have converted or not, defined as having more than one package or not.

In the overview on the left, you'll see the client information, which contains their name, email, mobile number, primary location, and when they first joined or attended. The mobile number and email are linked to the WhatsApp and email buttons at the top. If you want to send a WhatsApp to the client, you can simply click the WhatsApp button, and it will open WhatsApp on your laptop or phone, allowing you to send them a message. Similarly, the email button will open the mail client on your phone or laptop to email the client directly.

Program details on the right contain the details of the LEA coach and collaborating coaches, showing who else has been involved in this client journey, excluding the lead coach. You'll also see when their current program started and ended, and their assessment status is contained below for a quick overview of the information submitted or not.

For this client, you can see that they've submitted their pre-health consult on intake form and all their initial assessment reports, allowing the initial report to be generated. There's a button to generate the initial report. The review assessments have been partially submitted, with the movement and metabolic screen assessments completed, but the embodied nutrition and training plan hasn't. Once those three are submitted, this will change to generate review report, allowing you to generate the review report.

The final component of the overview section is the client journey, providing a visual overview of the various milestones within this client's journey. This is probably more specific to English or program clients but is still relevant to all clients. You can see when they joined and had their first session at UFIT, when they had each of their initial nutrition, metabolic, and performance screening sessions, when the consult was, when the program started, and the two-week, six-week, and 10-week check-in milestones. There are also forward-looking dates towards when the program end is moving over to.

The next component is the sessions component. So, here it's broken into two sections. The first section would be upcoming appointments. Upcoming appointments today or in the future will be within this section, so you'll be able to see what the client has scheduled coming up.

And then the session history is the second component. So, initially, you'll see a tile like this with just the date, the service type, who their service provider was, and then the session status. Essentially, when did they turn up? Did they late cancel or did they attend?

If you want some additional details with regards to that session, you can click on it to open. Some additional information, such as session count, duration, pricing, option use, et cetera, will be available.

The next section, which is visible, is check-ins, and this will give you an overview of whether check-ins have been completed. So, the two-week check-in, which will be submitted by the location manager, the six-week MBS survey, which is sent out automatically to the client, and the completion status of whether they have submitted that information or not.

And then, finally, the ten-week check-in, which is done by the location manager. The next section, which you'll see, is notes. These are private client notes just for the specific client in question. You can add a note here by simply clicking "Add a note," putting the information that you'd like to save here, and then clicking "Submit." There is a pin function as well.

So, if there's important information that you want to refer to or you want other people to refer to, such as people who are covering sessions in the future, you'll be able to pin those notes up towards the top here so they can be seen. You can search for specific notes using the search box over on the right here.

One feature, which is in development and not currently ready at the moment, once the physios have their client management system up and running, is the ability to set shared notes, which will be shared between professionals between the PT side and the Visio side.

Next tab is the assessments tab, and this will provide a little bit more information with regards to the assessment status of the client, with the pre-assessment data, pre-health consort health consultant in tech form, and then the assessment reports that have been submitted. And then, we'll provide information with regards to specific metrics, which are displayed below the pre-assessment data. If you click on that area, you'll be able to view what information the client submitted. There'll be a dialog box that opens there. Same for Health Consult, and then same for the intake form.

So, you'll be able to refer to pertinent information and details there. Once those five initial assessment reports have been populated, you will see the "Generate Initial Report" up here on the right. And then, once the five review assessment reports have been submitted, you'll see a "Generate Reviewer Report" button here on the right. 
We'll run through what that looks like in just a second. The assessment metrics will be displayed below when there has only been one metric submitted. This is the view that you will see, and it's just a simple display of the information that's been provided. Once the client has had at least one review assessment provided, you will then see a comparison between the initial value and the review value. 
And then, once more than one review data point has been provided, that would then be populated into this chart. So, you'll see multiple reviews and their progress over time. You'll see a comparison between their initial value and their latest value below, and then an overview of their improvement in percentage terms. The simple color coding with each of these metrics is that if the metric has improved, the bar chart will be green. 
If it has not improved or if it's gotten worse, the bar chart will be red. So, when you're screening through, you'll be able to easily see the metrics that have improved and the metrics that have not improved. 
And then, at the foot of this section, there are some bar charts. So, as these data points populate over time, you'll be able to see the trajectory of change over time. For generating the initial assessment report, it's very simple. You just click that button, a dialog box will open, and this takes up to about sixty seconds to complete to collect all of the relevant data and start to populate the document. I'm just going to pause while we wait for that to complete. 
Okay, so it's been going for about 45 seconds now. It should complete in the next few seconds. When it completes, what we should see is that this dialog box changes to allow you to view the report. 
Yeah, or directly download the report. So, if you click "View the report," it will just open in a new tab on your browser, and you'll be able to view the report directly. Or, you can download the report to your device to be able to share the PDF directly. 
Okay. And then, talking about reports, this "Reports" tab is essentially a repository of reports that have been generated historically. Because this is a test account, obviously, we can see that there's a number of initial assessments that have been generated. But for most clients, you're only going to see one. Then, as their review assessments are generated, you'll be able to see them populate here. This means that you can always refer back to this section to be able to either download or view historical reports to be able to share them with a client if they lose it.

For example, and then the final section is the success metric section. So, the most recent submitted success update will be highlighted at the top here. If the client has not yet had a success update, if they are an ignition client, what will happen is it will pull the metrics from their initial assessment reports. So, they have a target that's set by the nutrition team and they have two targets that are set by their fitness coach, and then they have one overarching goal that should be populated automatically when you click "add success metric."

If this is a historical client, those initial metrics will have to be manually populated and copied across from the current system that you use. But if I click "add success metric" just to give you an overview of what's shown here, we can see the goal when you add your update, and we can add some information here. And then you can do the same for each of the targets. And then there's an additional "dial additional comments" box at the bottom for just additional context with regards to the update or information that you'd like to share.

If a client has updated their target, sorry, has achieved their target and their target requires updating, once you click "Target achieved," you'll be required to update their target here. And what will happen is you set the new target, and it will record that a new target has been set in the box below. You can add further comments here with no problems. And then once they've submitted, what will happen is that you'll see the update that you just made, so that they've achieved that target, they've had a new target that's set, and whatever additional further comments that you've made.

But then the latest entry within the Success metric will include the new target that you've just set. Obviously, there is no commentary on this because it's a new set target, and the way to identify that is, you'll see a comment here that says "auto-created from updated goals and targets."
`
    },
    {
      id: 'reports',
      title: 'Retrieving Reports',
      description: 'Learn how to search, retrieve and view reports',
      category: 'Reports',
      embedUrl: 'https://player.vimeo.com/video/1074628895?h=db398f72c1&badge=0&autopause=0&player_id=0&app_id=58479',
      duration: '01:37',
      transcript: `This is the report system of the client management system, where you can quickly search for and retrieve any of the generated reports for your clients. At the top, you'll see some super quick metrics with regards to the volume of reports that have been generated, client volume, etc.
Then you have a filter search bar. So, if you know the client's name or client ID, you can just search that directly here. Or you can choose the client from the drop-down and filter by report type and location. At the moment, this is just populated with one client.
But as you create more reports, more and more reports, more and more clients will populate here. There are three sections. First, section is just a repository containing all reports. The best way to search within that is to search by the client name or use the filter boxes at the top. Then, when you go to a client, you'll see different components that just contain client reports that are organized in chronological order per client, and then finally, a timeline so you can see the most recently generated reports here.
If you want to view it in the browser, you can simply click View report. If you prefer to download it in PDF version, you can click the download report here. It will then be downloaded to your device and you can distribute it accordingly.`
    },
    {
      id: 'notepad-tasks',
      title: 'Notepad & Task Management',
      description: 'Learn how to use the notepad and task management features',
      category: 'Personal Productivity',
      embedUrl: 'https://player.vimeo.com/video/1074629748?h=b4399bc6c4&badge=0&autopause=0&player_id=0&app_id=58479',
      duration: '02:14',
      transcript: `This is the notepad and task management section of the client management system. This is here purely for your own use, and everything within this component will be private to you.
So you can create a personal note, that could be a reminder, or anything you like, really, but very simply click Add New Note. Click Save Note, and the note will be saved here. You can then subsequently edit if you want to change it or delete it if it's no longer required. The task manager is fairly simple. You can add your own tasks here with a due date. I'm just going to add a task for us, and then it will sit here in a list detailing your active tasks. Once a task has been completed, you click the tick, and then it will move it over to the completed tasks.
Then you can choose to leave it here, just for it to sit there crossed out, or if you want to clear it, you can click the purple box, which is an archive button, it will remove the task from the list completely. You'll notice over here, under Active Tasks, there are a couple labeled as assigned to you.
That is a function where the fitness lead at your location or your home manager can assign tasks directly to your profile. It might be that you need to follow up with a client or some other task, but then that will sit within your task list. When you complete that, it will show as per the previous in your completed tasks, but your manager or fitness lead will be able to see that assigned task has been completed, so it sends a notification to them that you've completed that task.`
    },
    {
      id: 'feedback',
      title: 'Feedback',
      description: 'How to use the feedback feature and what type of feedback to send.',
      category: 'Feedback',
      embedUrl: 'https://player.vimeo.com/video/1074632188?h=a5cbf6c701&badge=0&autopause=0&player_id=0&app_id=58479',
      duration: '02:45',
      transcript: `This is the feedback section of the client management system. All feedback and suggestions are really highly valued and will help to direct the future development of the system. Essentially the end goal is to have one central resource that you use to manage clients and manage your day to day workflows.
And we want to try and make that as efficient, effective and optimal as possible. So, a all feedback is, yeah, really highly valued and will be, thoughtfully considered. But I'd really like to hear what ideas you might have or suggestions you might have with regards to what features or functions that could be implemented that could actually make this system more useful or help to optimize your systems and processes.
And ultimately help deliver more value to clients. And those can be little things, you know, no suggestion is too small, but they can be pretty bold pretty big and bold things as well. So I would please encourage you, if you have an idea that's percolating and ruminating in your head, to please submit that it may be that, I would need to reach out and just get a little bit more clarity with regards to what that might look like.
But ultimately, this that will help to shape the future of how this system evolves. So simply, the, form just contains a little bit of information with regards to upgrades that are currently planned and in the pipeline at the next major revision of the system.
And just for timeline, we're expecting that to be within the next two to three months with some additional functionality to be added. And then below is a simple web form requesting your details, and then textbox to add the feedback and suggested enhancements. This will come through straight to you. To me.
And as I say, it will be really highly appreciated and highly valued and it will help to give me some further ideas and inspiration with regards to how we develop this further and how we optimize this. The system for, your usage on the ground.`
    },
    {
      id: 'password-reset',
      title: 'Password Reset',
      description: 'Learn how to reset your password',
      category: 'Personal',
      embedUrl: 'https://player.vimeo.com/video/1074633361?h=352579c714&badge=0&autopause=0&player_id=0&app_id=58479',
      duration: '00:56'
    }
  ];

  const textGuides: TextGuide[] = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      content: `
## Client Management Dashboard Overview
## Summary Tiles

Total Clients: Active clients who have attended a session since January 1, 2025,

Active Clients: Clients with current active packages

Pending Reports: Clients eligible for report generation awaiting action

Success Updates: Clients requiring success update action

Upcoming Sessions: Sessions scheduled for today or tomorrow

## Component Details
### Ignition Client Alerts
Flags clients missing prerequisite sessions at key milestones:

2-week milestone: Missing accessory sessions (metabolic screen, physical performance, nutrition)
6-week milestone: Missing second nutrition session
12-week milestone: Any outstanding sessions at program completion

### Pending Reports

Displays clients who have fulfilled criteria for report generation
Click-through functionality to access client profiles and generate reports

### Upcoming Sessions

Lists today's and tomorrow's scheduled sessions
Expandable view with direct access to client profiles

### Recent Sessions

Chronological list of clients by most recent session date
Expandable to view more clients beyond initial display

### Success Updates Required

Clients needing success updates (6+ weeks since last update with active package)
Direct access to client profiles for submitting updates
Note: Only appears after at least one success update has been recorded

### Recent Subscription Cancellations

Centralized view of clients who have canceled ongoing subscriptions

### Performance Indicators

Ignition Conversion Rate: Percentage of ignition program clients converting to subsequent programs
First Cycle Conversion Rate: Broader view of all program conversion rates
NPS Score: Average client rating (out of 5) collected at 6-week mark
Average Sessions Per Client: Session frequency for active clients

### Sessions Per Month

13-month historical view of all session types
Includes one-to-one, group, and class sessions
Provides overview of monthly session volume trends

      `,
      category: 'Dashboard',
      icon: <DashboardIcon />
    },
    {
      id: 'client-management-guide',
      title: 'Client Management',
      content: `
## Client Overview Tab

### Client Classifications
- Your Clients: Clients where you are the lead coach (defined as the coach seen most frequently in the client's most recent 12 visits)
- Collaborator Clients: Clients you've seen but aren't the lead coach for (e.g., covering sessions, providing accessory assessments)

### Client List Information
- Client ID
- Client name
- Date of first visit
- Most recent visit
- Lead coach name
- Location
- Report status (for Ignition program 2025 clients)
- Package status (active/inactive)
- Conversion status (whether client purchased subsequent packages)

### Navigation Features
- View more rows or use pagination arrows for large client lists
- Search by name, ID, or email
- Filter between active and inactive clients

## Client Details Page

### Header Information
- Client name
- Package status (active/inactive)
- Client ID number
- Most recent attendance
- Preferred location
- Conversion status

### Client Information Section
- Personal details (name, email, mobile, primary location, join date)
- Direct communication buttons (WhatsApp and email)

### Program Details Section
- Lead coach information
- Collaborating coaches
- Program start/end dates
- Assessment status overview

### Client Journey Timeline
- Visual representation of client milestones
- Key dates (join date, first session, assessment sessions)
- Program checkpoints (2-week, 6-week, 10-week)
- Program end date

### Sessions Component
- Upcoming Appointments: Scheduled future sessions
- Session History: Past sessions with details
  - Date, service type, provider, attendance status
  - Expandable for additional details (session count, duration, pricing)

### Check-ins Section
- Two-week check-in (submitted by location manager)
- Six-week NPS survey (automatically sent to client)
- Ten-week check-in (done by location manager)

### Notes Section
- Private client notes
- Add note functionality
- Pin important information feature
- Search notes capability
- Future feature: shared notes with physiotherapy team

### Assessments Tab
- Pre-assessment data
- Health consult information
- Intake form details
- Assessment reports status
- Report generation buttons (initial and review)

### Assessment Metrics
- Display of individual metrics
- Comparison view (initial vs. review values)
- Progress visualization
  - Green bars for improved metrics
  - Red bars for unchanged/worsened metrics
- Trajectory charts showing change over time

### Reports Tab
- Repository of all generated reports
- View and download functionality for sharing

### Success Metrics Section
- Most recent success update highlighted
- For new Ignition clients: pre-populated with initial assessment targets
- For historical clients: requires manual entry

### Success update workflow:
  - Record progress on goals and targets
  - Mark targets as achieved
  - Set new targets when achieved
  - Add additional comments
  - Automatically tracks target updates and changes
      `,
      category: 'Client Management',
      icon: <PeopleIcon />
    },
    {
      id: 'reports-guide',
      title: 'Reports Guide',
      content: `

## Overview
- Central repository for accessing and managing all client reports
- Quick metrics dashboard showing report generation volume and client statistics

## Search and Filter Features
- Search bar for finding reports by client name or ID
- Client dropdown menu for selection
- Filter options by report type and location
- Results populate as more reports are generated

## Report Organization Sections

### All Reports Repository
- Complete collection of all generated reports
- Best accessed using the search function or filter options

### Client-Specific Reports
- Reports organized chronologically by client
- Individual client sections for easy navigation

### Timeline View
- Displays most recently generated reports
- Quick access to newest content

## Report Access Options
- View Report: Opens report in browser for immediate viewing
- Download Report: Saves PDF version to device for distribution
      `,
      category: 'Reports',
      icon: <AssessmentIcon />
    },
    {
      id: 'notepad-tasks-guide',
      title: 'Notepad and Task Management',
      content: `
## Personal Notes
- Private section for your personal use only

### Creating Notes
  - Click "Add New Note"
  - Enter content
  - Click "Save Note"

### Managing Notes
  - Edit option for modifying existing notes
  - Delete option for removing unwanted notes

## Task Manager

### Active Tasks
- Create personal tasks with due dates
- View list of all pending tasks

### Task Types
- Self-created tasks
- Tasks assigned by fitness lead or manager

### Completed Tasks
- Mark tasks as complete by clicking the checkmark
- Completed tasks display with strikethrough

### Task Management Options
- Keep completed tasks visible in the list
- Archive tasks (purple button) to remove them completely

### Assigned Tasks Feature
- Tasks can be assigned directly to you by:
  - Fitness lead at your location
  - Manager
- Completing assigned tasks sends automatic notification to the assigner
- System tracks completion status for management visibility
      `,
      category: 'Notepad and Task Management',
      icon: <NotepadIcon />
    },
    {
      id: 'feedback-guide',
      title: 'Feedback',
      content: `

## Purpose
- Collect user input to guide future system development
- Create a central resource that optimizes client management and daily workflows
- Improve efficiency and effectiveness of the platform
- Ultimately deliver more value to clients

## Feedback Process
- All feedback and suggestions are highly valued
- No suggestion is too small or too ambitious
- Ideas will be thoughtfully considered for implementation
- Follow-up may occur for clarification on submitted ideas

## Current Status
- Next major revision planned within 2-3 months
- Additional functionality scheduled for upcoming updates

## Submission Form
- Basic user details required
- Text box for feedback and enhancement suggestions
- Submissions sent directly to system administrators
- All contributions appreciated as they help shape future development
      `,
      category: 'Feedback',
      icon: <FeedbackIcon />
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Guides & Documentation
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Learn how to use the Client Management System effectively with our comprehensive guides and video tutorials.
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="guides tabs"
          >
            <Tab label="Video Tutorials" id="guide-tab-0" aria-controls="guide-tabpanel-0" />
            <Tab label="Written Guides" id="guide-tab-1" aria-controls="guide-tabpanel-1" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" component="h2" gutterBottom>
            Video Tutorials
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Watch these video tutorials to quickly learn how to use the system.
          </Typography>

          <Grid container spacing={3}>
            {videoGuides.map(guide => (
              <Grid item xs={12} sm={6} md={4} key={guide.id}>
                <Card elevation={1}>
                  <CardActionArea onClick={() => handleVideoClick(guide)}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            borderRadius: '50%',
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2
                          }}
                        >
                          <PlayIcon />
                        </Box>
                        <Typography variant="h6" component="div" noWrap>
                          {guide.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {guide.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Typography variant="caption" color="primary">
                          {guide.category}
                        </Typography>
                        {guide.duration && (
                          <Typography variant="caption" color="text.secondary">
                            {guide.duration}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Vimeo Player Script */}
          <Box component="div" sx={{ display: 'none' }}>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </Box>
          
          {/* Video Modal */}
          <Dialog
            open={isVideoModalOpen}
            onClose={handleCloseVideoModal}
            maxWidth="lg"
            fullWidth
          >
            {selectedVideo && (
              <>
                <DialogTitle>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{selectedVideo.title}</Typography>
                    <IconButton edge="end" color="inherit" onClick={handleCloseVideoModal} aria-label="close">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                      <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', width: '100%', mb: 2 }}>
                        <iframe
                          src={selectedVideo.embedUrl}
                          title={selectedVideo.title}
                          style={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                            borderRadius: theme.shape.borderRadius
                          }}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                          allowFullScreen
                        ></iframe>
                      </Box>
                      <Typography variant="body1" gutterBottom>{selectedVideo.description}</Typography>
                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography variant="body2" color="primary">Category: {selectedVideo.category}</Typography>
                        {selectedVideo.duration && (
                          <Typography variant="body2" color="text.secondary">Duration: {selectedVideo.duration}</Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography variant="h6" gutterBottom>Transcript</Typography>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'grey.50', 
                          borderRadius: 2,
                          height: { xs: '200px', md: '400px' },
                          overflow: 'auto'
                        }}
                      >
                        {selectedVideo.transcript ? (
                          <Typography 
                            variant="body2" 
                            component="pre" 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'inherit'
                            }}
                          >
                            {selectedVideo.transcript}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center">
                            No transcript available for this video.
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </DialogContent>
              </>
            )}
          </Dialog>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" component="h2" gutterBottom>
            Written Documentation
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Detailed guides for all aspects of the Client Management System.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <List
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
                component="nav"
              >
                <ListItem>
                  <Typography variant="subtitle2">Guide Categories</Typography>
                </ListItem>
                <Divider />
                {['Dashboard', 'Client Management', 'Reports', 'Productivity', 'Feedback'].map((category) => (
                  <ListItem button key={category}>
                    <ListItemIcon>
                      {category === 'Dashboard' && <DashboardIcon color="primary" />}
                      {category === 'Client Management' && <PeopleIcon color="primary" />}
                      {category === 'Reports' && <AssessmentIcon color="primary" />}
                      {category === 'Productivity' && <NotepadIcon color="primary" />}
                      {category === 'Feedback' && <FeedbackIcon color="primary" />}
                    </ListItemIcon>
                    <ListItemText primary={category} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={9}>
              {textGuides.map((guide) => (
                <Accordion key={guide.id} sx={{ mb: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${guide.id}-content`}
                    id={`${guide.id}-header`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ mr: 2, color: 'primary.main' }}>
                        {guide.icon}
                      </Box>
                      <Typography variant="subtitle1">{guide.title}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        '& h2': {
                          fontSize: '1.5rem',
                          fontWeight: 500,
                          mb: 2
                        },
                        '& h3': {
                          fontSize: '1.2rem',
                          fontWeight: 500,
                          mb: 1,
                          mt: 3
                        },
                        '& ul, & ol': {
                          pl: 2,
                          mb: 2
                        },
                        '& li': {
                          mb: 1
                        }
                      }}
                    >
                      <Typography
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: guide.content
                            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
                            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
                            .replace(/- (.*$)/gim, '<li>$1</li>')
                            .replace(/<\/li>\n<li>/g, '</li><li>')
                            .replace(/^\s*\n\*/gim, '<ul><li>')
                            .replace(/\n\s*\n/g, '</p><p>')
                            .replace(/\n\d\. /g, '</p><ol><li>')
                            .replace(/<\/li>\n/g, '</li></ul></p><p>')
                        }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Guides;
